import { useEffect, useState } from "react";
import api, { API, apiError, imageById } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, Input, Textarea } from "@/pages/admin/managers/ui";
import { Upload, Save, X } from "lucide-react";

const Card = ({ title, children }) => (
  <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-6">
    <h3 className="font-serif text-xl text-white mb-4">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

const ImageUploader = ({ label, value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bookings/upload-reference", fd);
      onChange(`${API}/images/${data.id}`);
      toast.success("Imagen subida.");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        {value && <img src={value} alt="" className="w-16 h-16 rounded object-cover border border-[#27272A]" />}
        <div className="flex-1 space-y-2">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL o sube una imagen" />
          <label data-testid={testid} className="inline-flex items-center gap-2 text-xs text-[#C5A059] cursor-pointer hover:text-[#E5C887]">
            <Upload size={14} /> {busy ? "Subiendo…" : "Subir imagen"}
            <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => upload(e.target.files[0])} />
          </label>
        </div>
      </div>
    </Field>
  );
};

const SlidesEditor = ({ slides, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const list = Array.isArray(slides) ? slides : [];
  const upload = async (files) => {
    setBusy(true);
    let arr = [...list];
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/bookings/upload-reference", fd);
        arr = [...arr, `${API}/images/${data.id}`];
        onChange(arr);
      }
      toast.success("Fotos agregadas al carrusel.");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <span className="block text-xs text-[#8C8375] mb-1.5 font-mono uppercase tracking-wide">Fotos del carrusel (rotan automáticamente en la portada)</span>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {list.map((src, i) => (
            <div key={i} className="relative w-20 h-20 rounded overflow-hidden border border-[#27272A]">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button data-testid={`slide-remove-${i}`} onClick={() => remove(i)} className="absolute top-0 right-0 bg-black/70 text-white p-0.5 hover:bg-red-500"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Pega una URL y agrega" />
        <Btn variant="subtle" onClick={() => { if (url.trim()) { onChange([...list, url.trim()]); setUrl(""); } }}>Añadir</Btn>
      </div>
      <label data-testid="slides-upload" className="inline-flex items-center gap-2 text-xs text-[#C5A059] cursor-pointer hover:text-[#E5C887] mt-2">
        <Upload size={14} /> {busy ? "Subiendo…" : "Subir fotos (varias)"}
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => upload(e.target.files)} />
      </label>
      <p className="text-xs text-[#8C8375] mt-2">Además, las portadas de cada categoría se muestran automáticamente en la rotación.</p>
    </div>
  );
};

export default function SettingsManager() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);

  const upd = (section, key, val) => setS((prev) => ({ ...prev, [section]: { ...prev[section], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s, time_slots: (typeof s.time_slots === "string" ? s.time_slots.split(",") : s.time_slots).map((x) => x.trim()).filter(Boolean) };
      await api.put("/admin/settings", payload);
      toast.success("Configuración guardada.");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  if (!s) return <p className="text-[#8C8375]">Cargando…</p>;
  const hero = s.hero || {}, about = s.about || {}, contact = s.contact || {}, brand = s.brand || {}, texts = s.texts || {};
  const slots = Array.isArray(s.time_slots) ? s.time_slots.join(", ") : s.time_slots;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex justify-end sticky top-20 z-10">
        <Btn data-testid="settings-save-btn" onClick={save} disabled={saving}><Save size={16} className="inline mr-1" /> {saving ? "Guardando…" : "Guardar cambios"}</Btn>
      </div>

      <Card title="Marca / Logo">
        <ImageUploader label="Logo (transparente, pequeño)" testid="logo-upload" value={brand.logo_url} onChange={(v) => upd("brand", "logo_url", v)} />
        <Field label="Nombre del sitio"><Input value={brand.site_name || ""} onChange={(e) => upd("brand", "site_name", e.target.value)} /></Field>
      </Card>

      <Card title="Portada (Hero)">
        <ImageUploader label="Imagen principal (respaldo)" testid="hero-image-upload" value={hero.image_url} onChange={(v) => upd("hero", "image_url", v)} />
        <SlidesEditor slides={hero.slides} onChange={(v) => upd("hero", "slides", v)} />
        <Field label="Título"><Input data-testid="hero-title-input" value={hero.title || ""} onChange={(e) => upd("hero", "title", e.target.value)} /></Field>
        <Field label="Subtítulo"><Input value={hero.subtitle || ""} onChange={(e) => upd("hero", "subtitle", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Botón 1 texto"><Input value={hero.btn1_text || ""} onChange={(e) => upd("hero", "btn1_text", e.target.value)} /></Field>
          <Field label="Botón 2 texto"><Input value={hero.btn2_text || ""} onChange={(e) => upd("hero", "btn2_text", e.target.value)} /></Field>
        </div>
      </Card>

      <Card title="Sobre mí">
        <ImageUploader label="Foto de Yocaira" testid="about-image-upload" value={about.image_url} onChange={(v) => upd("about", "image_url", v)} />
        <Field label="Texto"><Textarea rows={5} data-testid="about-text-input" value={about.text || ""} onChange={(e) => upd("about", "text", e.target.value)} /></Field>
      </Card>

      <Card title="Contacto y redes">
        <div className="grid grid-cols-2 gap-3">
          <Field label="WhatsApp (con código país)"><Input data-testid="contact-whatsapp-input" value={contact.whatsapp || ""} onChange={(e) => upd("contact", "whatsapp", e.target.value)} placeholder="19172598010" /></Field>
          <Field label="Teléfono"><Input value={contact.phone || ""} onChange={(e) => upd("contact", "phone", e.target.value)} placeholder="917-259-8010" /></Field>
          <Field label="Instagram (usuario)"><Input value={contact.instagram || ""} onChange={(e) => upd("contact", "instagram", e.target.value)} /></Field>
          <Field label="Correo electrónico"><Input value={contact.email || ""} onChange={(e) => upd("contact", "email", e.target.value)} /></Field>
          <Field label="Área de servicio"><Input value={contact.service_area || ""} onChange={(e) => upd("contact", "service_area", e.target.value)} /></Field>
          <Field label="Horario de atención"><Input value={contact.hours || ""} onChange={(e) => upd("contact", "hours", e.target.value)} /></Field>
        </div>
        <Field label="Mensaje predeterminado de WhatsApp"><Input value={contact.whatsapp_message || ""} onChange={(e) => upd("contact", "whatsapp_message", e.target.value)} /></Field>
      </Card>

      <Card title="Reservaciones">
        <Field label="Horarios disponibles (separados por coma)"><Input data-testid="time-slots-input" value={slots || ""} onChange={(e) => setS({ ...s, time_slots: e.target.value })} /></Field>
        <Field label="Nota sobre el depósito"><Textarea rows={2} value={s.deposit_note || ""} onChange={(e) => setS({ ...s, deposit_note: e.target.value })} /></Field>
      </Card>

      <Card title="Textos de secciones">
        {[
          ["portfolio_title", "Título Portafolio"], ["portfolio_subtitle", "Subtítulo Portafolio"],
          ["services_title", "Título Servicios"], ["services_subtitle", "Subtítulo Servicios"],
          ["packages_title", "Título Paquetes"], ["packages_subtitle", "Subtítulo Paquetes"],
          ["testimonials_title", "Título Testimonios"], ["testimonials_subtitle", "Subtítulo Testimonios"],
        ].map(([k, label]) => (
          <Field key={k} label={label}><Input value={texts[k] || ""} onChange={(e) => upd("texts", k, e.target.value)} /></Field>
        ))}
      </Card>

      <div className="flex justify-end pb-10">
        <Btn onClick={save} disabled={saving}><Save size={16} className="inline mr-1" /> {saving ? "Guardando…" : "Guardar cambios"}</Btn>
      </div>
    </div>
  );
}
